import type { RowData } from "@tanstack/react-table";
import type { RecordColumnDef } from "@/components/ui/record-table/column-types";

export {
  FLEXIBLE_COLUMN_MAX_WIDTH,
  flexibleWidth,
  resolveGrowMaxWidth,
} from "@/components/ui/record-table/grid/grid-widths";
export type { RecordColumnMeta } from "@/components/ui/record-table/grid/grid-widths";

export type AccessorFn<TData extends RowData, TValue> = (row: TData) => TValue;

import type { BadgeVariant } from "@/lib/labels/types";
export type { BadgeVariant };

export type BadgeConfig = {
  label: string;
  variant: BadgeVariant;
};

export interface BaseColumnConfig {
  id?: string;
  header: string;
  size?: number;
  growMaxWidth?: number;
  meta?: Record<string, unknown>;
  enableSorting?: boolean;
}

export type ColumnFactory<TData extends RowData> = RecordColumnDef<TData, unknown>;

export function getBadgeConfig<TItem extends string>(
  badges: Record<TItem, BadgeConfig>,
  value: string | undefined | null,
  fallback: BadgeConfig,
): BadgeConfig {
  if (!value) return fallback;
  const lookup: Partial<Record<string, BadgeConfig>> = badges;
  return lookup[value] ?? fallback;
}

export function resolveColumnId<TData extends RowData>(
  accessor: keyof TData | AccessorFn<TData, unknown>,
  header: string,
): string {
  return typeof accessor === "string"
    ? accessor
    : header.toLowerCase().replace(/\s+/g, "_");
}

export function resolveAccessorProps<TData extends RowData, TValue>(
  accessor: keyof TData | AccessorFn<TData, TValue>,
) {
  return {
    accessorKey: typeof accessor === "string" ? accessor : undefined,
    accessorFn:
      typeof accessor === "function"
        ? (row: TData) => accessor(row)
        : undefined,
  };
}

function readAccessor<TData extends RowData>(
  accessor: keyof TData | AccessorFn<TData, unknown>,
  row: TData,
): unknown {
  return typeof accessor === "function" ? accessor(row) : row[accessor];
}

export function resolveString<TData extends RowData>(
  accessor: keyof TData | AccessorFn<TData, string | undefined>,
  row: TData,
): string | undefined {
  const value = readAccessor(accessor, row);
  return typeof value === "string" ? value : undefined;
}

export function resolveNumber<TData extends RowData>(
  accessor: keyof TData | AccessorFn<TData, number | undefined>,
  row: TData,
): number | undefined {
  const value = readAccessor(accessor, row);
  return typeof value === "number" ? value : undefined;
}

export function resolveBoolean<TData extends RowData>(
  accessor: keyof TData | AccessorFn<TData, boolean | undefined>,
  row: TData,
): boolean | undefined {
  const value = readAccessor(accessor, row);
  return typeof value === "boolean" ? value : undefined;
}
